import { useEffect, useState, useMemo, useCallback } from 'react';
import { Brand, Country, getLinkChecklistToProduct, getTrackAndTraceChecklists, TChecklistTrackAndTraceItem, TGetLinkChecklistToProductResponse, TGetLinkChecklistToQuestionItem } from 'api';

type TUseTrackAndTraceCommonProps = {
  brand?: Brand;
  country?: Country;
}
export const useTrackAndTraceCheckLists = ({
  brand,
  country
}: TUseTrackAndTraceCommonProps) => {
  const [trackAndTraceChecklists, setTrackAndTraceChecklists] = useState<TChecklistTrackAndTraceItem[]>([]);
  const [trackAndTraceChecklistsMapByResourceKey, setTrackAndTraceChecklistsMapByResourceKey] = useState<Record<string, TChecklistTrackAndTraceItem>>({});

  
  useEffect(() => {
    if (!brand?.id || !country?.id) return;
    const fetchTrackAndTraceChecklists = async (brandId: string, countryIsoCode: string) => {
      try {
        const trackAndTraceChecklists = await getTrackAndTraceChecklists({
          brandId,
          countryIsoCode
        });
        setTrackAndTraceChecklists(trackAndTraceChecklists);
        const trackAndTraceChecklistsMap = trackAndTraceChecklists.reduce((acc, field) => {
          acc[field.nameResourceKey] = field;
          return acc;
        }, {} as Record<string, TChecklistTrackAndTraceItem>);
        setTrackAndTraceChecklistsMapByResourceKey(trackAndTraceChecklistsMap);

      } catch (error) {
        console.error('Failed to fetch track and trace checklists:', error);
        setTrackAndTraceChecklists([]);
        setTrackAndTraceChecklistsMapByResourceKey({});
      }
    };
    fetchTrackAndTraceChecklists(brand.id, country.id);
  }, [brand, country]);

  const hasCheckListquestionConfig = useMemo(() => !!trackAndTraceChecklists.length, [trackAndTraceChecklists]);

  return useMemo(() => ({
    trackAndTraceChecklists,  
    trackAndTraceChecklistsMapByResourceKey,
    hasCheckListquestionConfig
  }), [
    trackAndTraceChecklists,
    trackAndTraceChecklistsMapByResourceKey,
    hasCheckListquestionConfig
  ]);
};

export const useTrackAndTraceProductQuestions = (
  {
    brand,
    country
  }: TUseTrackAndTraceCommonProps
) => {
  const [trackAndTraceProductQuestionsMap, setTrackAndTraceProductQuestionsMap] = useState<Record<string, TGetLinkChecklistToQuestionItem[]>>({});

  useEffect(() => {
    if (!brand?.id || !country?.id) return;
    const fetchTrackAndTraceProductChecklists = async (brandId: string, countryIsoCode: string) => {
      try {
        const linkChecklistToProductResponse: TGetLinkChecklistToProductResponse = await getLinkChecklistToProduct({
          brandId,
          countryIsoCode
        });
        const productQuestionsMap = linkChecklistToProductResponse.reduce((acc, p) => {
          acc[p.productId] = p.questions;
          return acc;
        }, {} as Record<string, TGetLinkChecklistToQuestionItem[]>);
        setTrackAndTraceProductQuestionsMap(productQuestionsMap);
      } catch (error) {
        console.error('Failed to fetch track and trace product checklists:', error);
        setTrackAndTraceProductQuestionsMap({});
      }
    }
    fetchTrackAndTraceProductChecklists(brand.id, country.id);
  }, [brand, country]);

  const linkProductQuestions = useCallback((productId: string, questions: TGetLinkChecklistToQuestionItem[]) => {
    setTrackAndTraceProductQuestionsMap(prev => ({
      ...prev,
      [productId]: questions
    }));
  }, []);

  const unlinkProductQuestions = useCallback((productId: string) => {
    setTrackAndTraceProductQuestionsMap(prev => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  return useMemo(() => ({
    trackAndTraceProductQuestionsMap,
    linkProductQuestions,
    unlinkProductQuestions
  }), [
    trackAndTraceProductQuestionsMap,
    linkProductQuestions,
    unlinkProductQuestions
  ]);
}